import React, {FC} from 'react';
import cn from 'classnames';
import Image from 'next/image';

import {Button} from '@zoefin/design-system';

import {
  QUALIFIED_FIND_ADVISOR_URL,
  QUALIFIED_THRESHOLD,
  STANDARD_CONTINUE_URL,
} from '../../constants';

import styles from './styles.module.scss';

interface AdvisorCardProps {
  investableAssets: number;
}

const QualifiedAdvisorCard: FC = () => (
  <div className={cn(styles.card, styles.card__modQualified)}>
    <div className={styles.card__mobileLogo} aria-hidden="true">
      <Image
        src="/static/img/zoe-logo/Zoe Logo White.svg"
        alt=""
        width={82}
        height={24}
      />
    </div>
    <Image
      src="/static/img/financial-health-score/cta-qualified-chart.svg"
      alt=""
      width={603}
      height={234}
      className={styles.card__chart}
    />
    <div
      className={cn(styles.card__label, styles.card__labelWith)}
      aria-hidden="true">
      <span className={styles.card__labelCaption}>{'With An Advisor'}</span>
      <span className={styles.card__labelValue}>{'$3,424,237'}</span>
    </div>
    <div
      className={cn(styles.card__label, styles.card__labelWithout)}
      aria-hidden="true">
      <span className={styles.card__labelCaption}>{'Without An Advisor'}</span>
      <span className={styles.card__labelValue}>{'$1,693,177'}</span>
    </div>

    <div className={styles.card__fade} />

    <div className={styles.card__content}>
      <h3 className={styles.card__title}>Ready to improve your score?</h3>
      <p className={styles.card__subtitle}>
        An advisor in Zoe&apos;s network can help you turn these insights into a
        personalized plan to improve your financial life.
      </p>
      <Button
        as="anchor"
        href={QUALIFIED_FIND_ADVISOR_URL}
        target="_blank"
        rel="noopener noreferrer"
        size="large"
        config={{color: 'white', type: 'primary'}}>
        Find an advisor
      </Button>
    </div>

    <p className={styles.card__disclaimer}>
      Independent research has estimated that working with a financial advisor
      can add roughly 3% per year in net value through tax management, asset
      location, and behavioral coaching.
    </p>
  </div>
);

const StandardAdvisorCard: FC = () => (
  <div className={styles.card}>
    <div className={styles.card__mobileLogo} aria-hidden="true">
      <Image
        src="/static/img/zoe-logo/Zoe Logo White.svg"
        alt=""
        width={82}
        height={24}
      />
    </div>
    <Image
      src="/static/img/financial-health-score/cta-standard-decoration.svg"
      alt=""
      width={336}
      height={338}
      className={styles.card__decoration}
    />

    <div className={styles.card__content}>
      <p className={styles.card__subtitle}>Your score is just the beginning.</p>
      <h3 className={styles.card__title}>Stay ahead of your finances.</h3>

      <ul className={styles.card__items}>
        <li className={styles.card__item}>
          <span className={styles.card__bullet} aria-hidden="true">
            •
          </span>
          <div>
            <p className={styles.card__itemTitle}>Zoe Blog</p>
            <p className={styles.card__itemDescription}>
              Polish your financial literacy and read up on the personal finance
              topics that matter to you.
            </p>
          </div>
        </li>
        <li className={styles.card__item}>
          <span className={styles.card__bullet} aria-hidden="true">
            •
          </span>
          <div>
            <p className={styles.card__itemTitle}>Your Financial Checklist</p>
            <p className={styles.card__itemDescription}>
              Address the parts of your score that need attention with a
              financial checklist tailored to your stage in life.
            </p>
          </div>
        </li>
      </ul>

      <Button
        as="anchor"
        href={STANDARD_CONTINUE_URL}
        target="_blank"
        rel="noopener noreferrer"
        size="large"
        config={{color: 'white', type: 'primary'}}>
        Continue
      </Button>
    </div>
  </div>
);

const AdvisorCard: FC<AdvisorCardProps> = ({investableAssets}) =>
  investableAssets >= QUALIFIED_THRESHOLD ? (
    <QualifiedAdvisorCard />
  ) : (
    <StandardAdvisorCard />
  );

export default AdvisorCard;
